<template>
    <div class="change-password-wrapper">
        <HogarthIcLogo class="logo" />
        <h1>Reset Password</h1>
        <Form direction="vertical" class="change-password-form" @submit.prevent>
            <form-item v-if="$route.params.username" label="Code">
                <Input
                    v-model="form.code"
                    type="text"
                    placeholder="Code"
                    size="large"
                    @keyup.enter="changePassword"
                ></Input>
            </form-item>
            <form-item label="New Password">
                <Input
                    v-model="form.password"
                    type="password"
                    placeholder="New Password"
                    size="large"
                    @keydown.stop
                    @keyup.enter="changePassword"
                ></Input>
            </form-item>
            <form-item label="Confirm New Password">
                <Input
                    v-model="form.confirmPassword"
                    type="password"
                    placeholder="Confirm New Password"
                    size="large"
                    @keydown.stop
                    @keyup.enter="changePassword"
                ></Input>
            </form-item>
            <form-item class="change-password-btn-wrapper">
                <Button type="primary" size="large" class="change-password-btn primary-green" @click="changePassword">
                    Save
                </Button>
            </form-item>
        </Form>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapMutations } from "vuex";

import HogarthIcLogo from "@/components/HogarthIcLogo.vue";
import bus from "@/bus";
import type { Err } from "@/types/types";
import { authN } from "@/router";
import { AppRouteNames } from "@/router/constants";
import { completeNewPassword, forgotPasswordSubmit } from "@/services/Auth";

export default defineComponent({
    name: "ResetPassword",
    components: {
        HogarthIcLogo
    },

    emits: [],

    data() {
        return {
            form: {
                code: "",
                password: "",
                confirmPassword: ""
            }
        };
    },

    mounted() {
        if (!this.$route.params.username && !this.$route.query.forceResetPassword) {
            void this.$router.push({ name: AppRouteNames.LOGIN });
        }
    },

    methods: {
        ...mapMutations("auth", ["setIsAuthorized"]),
        validateChangePassword() {
            if (this.$route.params.username && this.form.code.length === 0) {
                this.$snackbar.fieldRequired("Code must not be empty");
                return false;
            }

            if (this.form.password.length === 0) {
                this.$snackbar.fieldRequired("Password must not be empty");
                return false;
            }

            if (this.form.password !== this.form.confirmPassword) {
                this.$snackbar.error("Password does not match", "Confirm Password must match the Password");
                return false;
            }
            return true;
        },
        async changePassword() {
            if (!this.validateChangePassword()) {
                return;
            }
            try {
                bus.$emit("percentLoadEvent", 50);
                // Call forgot password submit if username in params
                if (this.$route.params.username) {
                    await forgotPasswordSubmit(
                        this.$route.params.username as string,
                        this.form.code,
                        this.form.password
                    );
                    this.$snackbar.success("Password Changed", "Your password has been successfully updated.");
                    bus.$emit("percentLoadEvent", 100);
                    this.setIsAuthorized(false);
                    await this.$router.push({ name: AppRouteNames.LOGIN });
                } else {
                    await completeNewPassword(this.form.password);
                    bus.$emit("percentLoadEvent", 100);

                    this.setIsAuthorized(true);

                    const redirectRoute = authN(this.$route)!;
                    await this.$router.push(redirectRoute);
                }
            } catch (e) {
                bus.$emit("percentLoadEvent", 100);
                this.$snackbar.authentication(e as Err);
            }
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.change-password {
    height: auto;
    margin-top: 25px;
    padding: 10px 50px;

    &-form {
        width: 100%;
    }

    &-wrapper {
        width: 360px;
        text-align: center;
        min-height: 80vh;
        margin: 0 auto;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;

        h1 {
            color: $white;
            font-size: 32px;
            margin-bottom: $spacing-large;
        }

        .change-password-btn {
            width: 100%;

            &-wrapper {
                margin-top: 24px;
            }
        }

        .logo {
            margin-bottom: $spacing-largest;
        }
    }
}
</style>
